/* A usable company name, so a blank Company field never costs us a business.

   The sibling of nameFromEmail, written for the same reason and off the same
   evidence. On 2026-09-15 at 07:47 a company typed a need, waited 41 seconds
   for the draft, looked at its matches, filled in the other fields, pressed
   Sign up — and was refused, because the Company box was empty. The event
   says `ever_focused: false`: they never even clicked it. Twenty-four seconds
   on the form, one press, gone. That is a real business, lost at the last
   field, and the same form already derives a person's NAME rather than
   refusing over it.

   Two sources, in order of how much they are worth asserting.

   A company domain is the strong one: `hello@northsidedental.com` is
   Northside Dental and almost nothing else. Free providers are stripped out,
   because "Gmail" is not a company and putting it on a public posting is
   worse than any blank field.

   Failing that we return "", and the caller falls back to the person's own
   name. That is not a guess: organisation names are NOT unique in this schema
   (slug carries identity and disambiguates itself), person-named companies
   already exist on the board — org 48 is "Crista Oosting" — and it is a
   single editable text box on the next screen. A name somebody tidies up
   later is worth incomparably more than an account that was never created. */

// Free mail. A domain here says where somebody reads their email, not who
// they work for.
const FREE_MAIL = new Set([
  "gmail.com", "googlemail.com", "yahoo.com", "ymail.com", "hotmail.com",
  "outlook.com", "live.com", "msn.com", "aol.com", "icloud.com", "me.com",
  "mac.com", "comcast.net", "sbcglobal.net", "att.net", "verizon.net",
  "bellsouth.net", "cox.net", "charter.net", "earthlink.net", "protonmail.com",
  "proton.me", "gmx.com", "mail.com", "zoho.com", "yandex.com", "netzero.com",
  "juno.com", "writeme.com", "hmail.com",
]);

// Suffixes that are punctuation as far as a company name goes.
const TLDISH = new Set([
  "com", "net", "org", "io", "co", "us", "biz", "info", "inc", "llc", "ltd",
  "uk", "ca", "au", "de", "fr", "es", "it", "nl", "se", "no", "dk", "gov", "edu",
]);

/**
 * Best-effort company name from an email address, or "" when there is nothing
 * worth asserting. Never throws; safe on junk input.
 */
export function companyFromEmail(email) {
  const domain = String(email || "").split("@")[1] || "";
  if (!domain) return "";

  const host = domain.trim().toLowerCase();
  if (FREE_MAIL.has(host)) return "";

  const parts = host.split(".").filter(Boolean);
  // Drop the TLD, and a country code sitting behind it (acme.co.uk).
  const words = parts.filter((p) => !TLDISH.has(p));
  if (!words.length) return "";

  // "northside-dental" and "northside_dental" are both two words.
  const label = words
    .join(" ")
    .replace(/[._\-]+/g, " ")
    .replace(/\d+/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!label) return "";
  // A single letter or two is an abbreviation we would be guessing at.
  if (label.replace(/\s/g, "").length < 3) return "";

  return label
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
    .slice(0, 200);
}
